import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type ApplicationDocument,
  jobsService,
  type InterviewAnalysis,
  type Job,
  type JobStatus,
} from '../services/jobs.service';

const STATUS_LABELS: Record<JobStatus, string> = {
  APPLIED: 'Applied',
  HR: 'HR Interview',
  TECH: 'Technical',
  OFFER: 'Offer',
  REJECTED: 'Rejected',
};

const STATUS_STYLE: Record<JobStatus, string> = {
  APPLIED: 'bg-surface-container-highest text-on-surface-variant',
  HR: 'bg-primary/10 text-primary border border-primary/20',
  TECH: 'bg-primary/10 text-primary border border-primary/20',
  OFFER: 'bg-green-400/10 text-green-400 border border-green-400/20',
  REJECTED: 'bg-error/10 text-error border border-error/20',
};

const STATUSES: JobStatus[] = ['APPLIED', 'HR', 'TECH', 'OFFER', 'REJECTED'];

const INTERVIEW_TYPE_LABELS: Record<string, string> = {
  HR: 'HR Screen',
  TECHNICAL: 'Technical',
  CULTURAL: 'Cultural Fit',
  BEHAVIORAL: 'Behavioral',
  OTHER: 'Interview',
};

function StatusModal({
  current,
  onClose,
  onSave,
}: {
  current: JobStatus;
  onClose: () => void;
  onSave: (s: JobStatus) => void;
}) {
  const [selected, setSelected] = useState<JobStatus>(current);
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-surface-container rounded-xl p-6 w-full max-w-sm border border-outline-variant/20 shadow-2xl">
        <h2 className="text-base font-bold text-on-surface mb-4">Update Status</h2>
        <div className="space-y-2 mb-6">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setSelected(s)}
              className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-between ${
                selected === s
                  ? 'bg-primary/10 text-primary'
                  : 'text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface'
              }`}
            >
              {STATUS_LABELS[s]}
              {selected === s && <span className="material-symbols-outlined text-sm">check</span>}
            </button>
          ))}
        </div>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 rounded-md bg-surface-container-high text-on-surface text-sm font-medium hover:bg-surface-bright transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => { onSave(selected); onClose(); }}
            className="flex-1 px-4 py-2 rounded-md primary-gradient text-on-primary text-sm font-semibold"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

const MAX_VIDEO_SIZE = 200 * 1024 * 1024; // 200 MB

function AddInterviewModal({
  onClose,
  onSubmit,
  onCancelUpload,
  isSubmitting,
  uploadProgress,
  error,
}: {
  onClose: () => void;
  onSubmit: (file: File) => void;
  onCancelUpload: () => void;
  isSubmitting: boolean;
  uploadProgress: number | null;
  error: string | null;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [sizeError, setSizeError] = useState<string | null>(null);

  const isUploading = isSubmitting && uploadProgress !== null && uploadProgress < 100;
  const isAnalyzing = isSubmitting && (uploadProgress === null || uploadProgress >= 100);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    if (f && f.size > MAX_VIDEO_SIZE) {
      setSizeError(`File is too large (${(f.size / 1024 / 1024).toFixed(0)} MB). Maximum allowed is 200 MB.`);
      setFile(null);
      e.target.value = '';
    } else {
      setSizeError(null);
      setFile(f);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-surface-container rounded-xl p-6 w-full max-w-lg border border-outline-variant/20 shadow-2xl">
        <h2 className="text-base font-bold text-on-surface mb-1">Add Interview</h2>
        <p className="text-sm text-on-surface-variant mb-5">
          Upload your interview recording — AI will detect the type and generate a title automatically.
        </p>

        <div>
          <label className="block text-xs font-medium text-on-surface-variant mb-1.5">Video or audio file</label>
          <input
            type="file"
            accept="video/*,audio/*"
            onChange={handleFileChange}
            disabled={isSubmitting}
            className="block w-full text-sm text-on-surface-variant file:mr-4 file:rounded-md file:border-0 file:bg-primary/10 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-primary hover:file:bg-primary/20 disabled:opacity-50"
          />
          <p className="mt-2 text-xs text-on-surface-variant/70">
            Supported: mp4, mov, webm, mp3, m4a, wav. Max 200 MB.
          </p>
        </div>

        {sizeError && (
          <div className="mt-3 flex items-start gap-2 rounded-lg bg-error/10 border border-error/20 px-3 py-2.5">
            <span className="material-symbols-outlined text-error text-base shrink-0 mt-0.5">error</span>
            <p className="text-xs text-error">{sizeError}</p>
          </div>
        )}

        {isSubmitting && (
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between text-xs text-on-surface-variant">
              <span>{isUploading ? 'Uploading file…' : 'Analyzing with AI…'}</span>
              {isUploading && <span className="font-semibold tabular-nums">{uploadProgress}%</span>}
            </div>
            <div className="h-1.5 w-full rounded-full bg-surface-container-highest overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${isAnalyzing ? 'bg-tertiary animate-pulse w-full' : 'bg-primary'}`}
                style={isUploading ? { width: `${uploadProgress}%` } : undefined}
              />
            </div>
          </div>
        )}

        {error && (
          <div className="mt-4 flex items-start gap-2 rounded-lg bg-error/10 border border-error/20 px-3 py-2.5">
            <span className="material-symbols-outlined text-error text-base shrink-0 mt-0.5">error</span>
            <p className="text-xs text-error">{error}</p>
          </div>
        )}

        <div className="flex gap-3 mt-6">
          {isSubmitting ? (
            <button
              onClick={onCancelUpload}
              className="flex-1 px-4 py-2 rounded-md bg-error/10 text-error border border-error/20 text-sm font-medium hover:bg-error/20 transition-colors"
            >
              Cancel Upload
            </button>
          ) : (
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 rounded-md bg-surface-container-high text-on-surface text-sm font-medium hover:bg-surface-bright transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            onClick={() => file && onSubmit(file)}
            disabled={!file || isSubmitting}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md primary-gradient text-on-primary text-sm font-semibold disabled:opacity-50"
          >
            {isSubmitting && (
              <div className="w-3 h-3 border-2 border-on-primary/40 border-t-on-primary rounded-full animate-spin" />
            )}
            {isSubmitting
              ? isUploading
                ? `Uploading ${uploadProgress}%`
                : 'Analyzing…'
              : 'Upload Interview'}
          </button>
        </div>
      </div>
    </div>
  );
}

function InterviewCard({
  interview,
  onViewFeedback,
  onDelete,
}: {
  interview: InterviewAnalysis;
  onViewFeedback: () => void;
  onDelete: () => void;
}) {
  const createdAt = new Date(interview.createdAt).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
  const typeLabel = interview.interviewType
    ? (INTERVIEW_TYPE_LABELS[interview.interviewType] ?? interview.interviewType)
    : null;

  const isProcessing = interview.status === 'pending' || interview.status === 'processing';
  const isFailed = interview.status === 'failed';

  return (
    <article className="bg-surface-container-low rounded-xl border border-outline-variant/10 p-5 flex items-center gap-5">
      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
        <span className="material-symbols-outlined text-primary">smart_display</span>
      </div>
      <div className="flex-1 min-w-0">
        {typeLabel && (
          <span className="mb-2 inline-flex rounded-lg border border-outline-variant/20 bg-surface-container-high px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-on-surface-variant">
            {typeLabel}
          </span>
        )}
        <p className="text-sm font-semibold text-on-surface truncate">{interview.title || interview.fileName}</p>
        <p className="text-xs text-on-surface-variant mt-0.5">
          {createdAt} • {interview.fileName}
          {isFailed && interview.errorMessage && (
            <span className="text-error ml-2">• {interview.errorMessage}</span>
          )}
        </p>
      </div>
      {isProcessing ? (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-tertiary/10 text-tertiary text-xs font-bold border border-tertiary/20 shrink-0">
          <div className="w-3 h-3 border-2 border-tertiary/30 border-t-tertiary rounded-full animate-spin" />
          Processing
        </div>
      ) : isFailed ? (
        <div className="px-2.5 py-1 rounded-lg bg-error/10 text-error text-xs font-bold border border-error/20 shrink-0">
          Failed
        </div>
      ) : (
        <div className="px-2.5 py-1 rounded-lg bg-primary/10 text-primary text-xs font-bold border border-primary/20 shrink-0">
          {interview.score ?? 0}/100
        </div>
      )}
      <div className="flex items-center gap-2 shrink-0">
        {(isProcessing || isFailed) && (
          <button
            onClick={onDelete}
            className="flex items-center gap-1 px-3 py-2 bg-error/10 hover:bg-error/20 text-error border border-error/20 rounded-lg text-xs font-semibold transition-colors"
            title="Remove this entry"
          >
            <span className="material-symbols-outlined text-sm">delete</span>
          </button>
        )}
        <button
          onClick={onViewFeedback}
          disabled={isProcessing || isFailed}
          className="flex items-center gap-1.5 px-4 py-2 bg-surface-container-high hover:bg-surface-bright disabled:opacity-40 disabled:cursor-not-allowed text-on-surface rounded-lg text-xs font-semibold transition-colors"
        >
          <span className="material-symbols-outlined text-sm">insights</span>
          View Feedback
        </button>
      </div>
    </article>
  );
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getDocumentIcon(fileName: string, mimeType?: string) {
  const normalized = `${mimeType ?? ''} ${fileName}`.toLowerCase();
  if (normalized.includes('pdf')) return 'picture_as_pdf';
  if (normalized.includes('doc') || normalized.includes('word')) return 'description';
  if (normalized.includes('sheet') || normalized.includes('xls') || normalized.includes('csv')) return 'table_chart';
  if (normalized.includes('slide') || normalized.includes('ppt')) return 'slideshow';
  if (normalized.includes('zip') || normalized.includes('rar')) return 'folder_zip';
  return 'draft';
}

function ApplicationDocumentRow({
  jobId,
  document,
}: {
  jobId: string;
  document: ApplicationDocument;
}) {
  const [isOpening, setIsOpening] = useState(false);

  const openDocument = async () => {
    if (isOpening) return;
    setIsOpening(true);
    try {
      const url = await jobsService.getDocumentUrl(jobId, document.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } finally {
      setIsOpening(false);
    }
  };

  return (
    <article className="grid grid-cols-[auto,minmax(0,1fr),auto] items-center gap-4 px-4 py-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface-container-high shrink-0">
        <span className="material-symbols-outlined text-primary">{getDocumentIcon(document.fileName, document.mimeType)}</span>
      </div>
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-on-surface">{document.fileName}</p>
        <p className="mt-1 text-xs text-on-surface-variant">
          {formatFileSize(document.fileSize)} • {new Date(document.createdAt).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </p>
      </div>
      <button
        onClick={openDocument}
        disabled={isOpening}
        className="flex items-center gap-1.5 rounded-lg bg-surface-container-high px-3 py-2 text-xs font-semibold text-on-surface transition-colors hover:bg-surface-bright disabled:opacity-50"
      >
        <span className="material-symbols-outlined text-sm">{isOpening ? 'progress_activity' : 'open_in_new'}</span>
        Open
      </button>
    </article>
  );
}

function AddDocumentsModal({
  onClose,
  onSubmit,
  isSubmitting,
}: {
  onClose: () => void;
  onSubmit: (files: File[]) => void;
  isSubmitting: boolean;
}) {
  const [files, setFiles] = useState<File[]>([]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-2xl rounded-xl border border-outline-variant/20 bg-surface-container p-6 shadow-2xl">
        <h2 className="mb-1 text-base font-bold text-on-surface">Upload application documents</h2>
        <p className="mb-5 text-sm text-on-surface-variant">
          Add any file the company sent you: PDFs, DOCX, briefs, take-homes, spreadsheets, slide decks or archives.
        </p>

        <div className="space-y-4">
          <div className="rounded-xl border-2 border-dashed border-outline-variant/20 bg-surface-container-low p-5">
            <input
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.txt,.rtf,.xls,.xlsx,.csv,.ppt,.pptx,.zip,.rar,.7z,.png,.jpg,.jpeg"
              onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
              className="block w-full text-sm text-on-surface-variant file:mr-4 file:rounded-md file:border-0 file:bg-primary/10 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-primary hover:file:bg-primary/20"
            />
            <p className="mt-3 text-xs text-on-surface-variant/70">
              You can upload multiple files at once. Max 25MB per file.
            </p>
          </div>

          <div className="max-h-64 overflow-y-auto rounded-xl border border-outline-variant/10 bg-surface-container-low">
            {files.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 px-6 py-10 text-center">
                <span className="material-symbols-outlined text-4xl text-on-surface-variant/40">folder_open</span>
                <p className="text-sm text-on-surface-variant">No files selected yet</p>
              </div>
            ) : (
              <div className="divide-y divide-outline-variant/10">
                {files.map((file) => (
                  <div key={`${file.name}-${file.size}`} className="flex items-center justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-on-surface">{file.name}</p>
                      <p className="mt-1 text-xs text-on-surface-variant">{formatFileSize(file.size)}</p>
                    </div>
                    <span className="rounded-md bg-surface-container-high px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-on-surface-variant">
                      {file.name.split('.').pop() ?? 'file'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 rounded-md bg-surface-container-high px-4 py-2 text-sm font-medium text-on-surface transition-colors hover:bg-surface-bright disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => files.length > 0 && onSubmit(files)}
            disabled={files.length === 0 || isSubmitting}
            className="flex-1 rounded-md primary-gradient px-4 py-2 text-sm font-semibold text-on-primary disabled:opacity-50"
          >
            {isSubmitting ? 'Uploading...' : `Upload ${files.length || ''} file${files.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function JobDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [notes, setNotes] = useState('');
  const [notesSaved, setNotesSaved] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const { data: job, isLoading } = useQuery<Job>({
    queryKey: ['job', id],
    queryFn: () => jobsService.findOne(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const data = query.state.data;
      const hasProcessing = data?.interviews?.some(
        (i) => i.status === 'pending' || i.status === 'processing',
      );
      return hasProcessing ? 5000 : false;
    },
  });

  useEffect(() => {
    if (job) setNotes(job.notes ?? '');
  }, [job?.id]);

  const updateMutation = useMutation({
    mutationFn: (dto: { status?: JobStatus; notes?: string }) =>
      jobsService.update(id!, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job', id] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['job-stats'] });
    },
  });

  const uploadInterviewMutation = useMutation({
    mutationFn: (file: File) => {
      abortControllerRef.current = new AbortController();
      return jobsService.uploadInterview(
        id!, { file },
        (pct) => setUploadProgress(pct),
        abortControllerRef.current.signal,
      );
    },
    onSuccess: () => {
      setShowInterviewModal(false);
      setUploadProgress(null);
      queryClient.invalidateQueries({ queryKey: ['job', id] });
    },
    onError: (err) => {
      setUploadProgress(null);
      // AbortError = user cancelled intentionally, no error banner needed
      if (err instanceof DOMException && err.name === 'AbortError') {
        uploadInterviewMutation.reset();
      }
    },
  });

  const handleCancelUpload = () => {
    abortControllerRef.current?.abort();
    setShowInterviewModal(false);
    setUploadProgress(null);
  };

  const deleteInterviewMutation = useMutation({
    mutationFn: (interviewId: string) => jobsService.deleteInterview(id!, interviewId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['job', id] }),
  });

  const uploadDocumentsMutation = useMutation({
    mutationFn: (files: File[]) => jobsService.uploadDocuments(id!, { files }),
    onSuccess: () => {
      setShowDocumentsModal(false);
      queryClient.invalidateQueries({ queryKey: ['job', id] });
    },
  });

  const saveNotes = () => {
    updateMutation.mutate({ notes });
    setNotesSaved(true);
    setTimeout(() => setNotesSaved(false), 2000);
  };

  if (isLoading || !job) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const appliedDate = new Date(job.appliedAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const updatedDate = new Date(job.updatedAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  return (
    <div className="flex-1 overflow-y-auto bg-background">
      {/* Sticky top bar */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-outline-variant/15 flex items-center justify-between px-8 h-14">
        <div className="flex items-center gap-6">
          <button
            onClick={() => navigate('/jobs')}
            className="flex items-center gap-1 text-on-surface-variant hover:text-on-surface transition-colors text-sm font-medium"
          >
            <span className="material-symbols-outlined text-lg">arrow_back</span>
          </button>
          <nav className="flex items-center gap-2 text-xs font-semibold text-on-surface-variant tracking-widest uppercase">
            <Link to="/jobs" className="hover:text-on-surface transition-colors">Applications</Link>
            <span className="material-symbols-outlined text-[10px]">chevron_right</span>
            <span className="text-on-surface">{job.company}</span>
          </nav>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() =>
              navigate('/assistant', {
                state: {
                  activeJobContext: {
                    id: job.id,
                    company: job.company,
                    role: job.role,
                  },
                },
              })
            }
            className="flex items-center gap-2 px-4 py-1.5 bg-surface-container-high text-on-surface rounded-md font-medium text-sm hover:bg-surface-bright transition-all"
          >
            <span className="material-symbols-outlined text-base">auto_awesome</span>
            Ask AI
          </button>
          <button
            onClick={() => setShowStatusModal(true)}
            className="flex items-center gap-2 px-4 py-1.5 primary-gradient text-on-primary rounded-md font-semibold text-sm hover:opacity-90 transition-all"
          >
            Update Status
          </button>
        </div>
      </header>

      <main className="p-10 max-w-7xl mx-auto space-y-10">
        {/* Header block */}
        <section className="flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div className="space-y-4">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 rounded-xl bg-surface-container-high flex items-center justify-center border border-outline-variant/15 shrink-0">
                <span className="material-symbols-outlined text-primary text-3xl">work</span>
              </div>
              <div>
                <h2 className="text-4xl font-bold tracking-tight text-on-surface">{job.role}</h2>
                <div className="flex items-center gap-4 mt-2 flex-wrap">
                  <span className="text-xl font-medium text-on-surface-variant">{job.company}</span>
                  <span className="w-1.5 h-1.5 rounded-full bg-outline-variant/40" />
                  <span className="text-on-surface-variant">Remote</span>
                  <span className={`px-3 py-1 text-xs font-bold rounded-full ${STATUS_STYLE[job.status]}`}>
                    {STATUS_LABELS[job.status]}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-6 text-sm text-on-surface-variant/80 pt-1">
              <div className="flex items-center gap-1.5">
                <span className="material-symbols-outlined text-base">calendar_today</span>
                Applied {appliedDate}
              </div>
              <div className="flex items-center gap-1.5">
                <span className="material-symbols-outlined text-base">update</span>
                Last update: {updatedDate}
              </div>
              {job.jobUrl && (
                <a
                  href={job.jobUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-primary hover:underline"
                >
                  <span className="material-symbols-outlined text-base">open_in_new</span>
                  Job posting
                </a>
              )}
            </div>
          </div>
        </section>

        {/* Main bento grid */}
        <div className="grid grid-cols-12 gap-8">
          {/* Left column */}
          <div className="col-span-12 lg:col-span-8 space-y-8">
            {/* Interview Sessions */}
            <section className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold tracking-tight flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary">videocam</span>
                  Interview Sessions
                </h3>
                {(() => {
                  const isProcessing = job.interviews?.some(
                    (i) => i.status === 'pending' || i.status === 'processing',
                  );
                  return isProcessing ? (
                    <div className="flex items-center gap-2 px-4 py-1.5 rounded-md bg-tertiary/10 text-tertiary border border-tertiary/20 text-sm font-semibold">
                      <div className="w-3 h-3 border-2 border-tertiary/30 border-t-tertiary rounded-full animate-spin" />
                      Processing…
                    </div>
                  ) : (
                    <button
                      onClick={() => { uploadInterviewMutation.reset(); setUploadProgress(null); setShowInterviewModal(true); }}
                      className="flex items-center gap-2 px-4 py-1.5 bg-primary/10 text-primary border border-primary/20 rounded-md font-semibold text-sm hover:bg-primary/20 transition-all"
                    >
                      <span className="material-symbols-outlined text-base">add</span>
                      Add Interview
                    </button>
                  );
                })()}
              </div>
              {job.interviews && job.interviews.length > 0 ? (
                <div className="space-y-4">
                  {job.interviews.map((interview) => (
                    <InterviewCard
                      key={interview.id}
                      interview={interview}
                      onViewFeedback={() => navigate(`/jobs/${id}/interviews/${interview.id}`)}
                      onDelete={() => deleteInterviewMutation.mutate(interview.id)}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-surface-container-low rounded-xl border border-outline-variant/10 p-12 flex flex-col items-center justify-center text-center gap-3">
                  <span className="material-symbols-outlined text-on-surface-variant/40 text-4xl">videocam_off</span>
                  <p className="text-sm font-medium text-on-surface-variant">No interview sessions recorded yet</p>
                  <p className="text-xs text-on-surface-variant/60">Add your first interview session to track your progress</p>
                </div>
              )}
            </section>

            {/* Preparation Notes */}
            <section className="space-y-4">
              <h3 className="text-xl font-semibold tracking-tight flex items-center gap-2">
                <span className="material-symbols-outlined text-tertiary">edit_note</span>
                Preparation Notes
              </h3>
              <div className="bg-surface-container-low rounded-xl p-6 border border-outline-variant/10">
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={8}
                  placeholder={`• Research ${job.company}'s recent projects and culture\n• Prepare STAR format answers\n• Review the job description again\n• Prepare questions to ask the interviewer`}
                  className="w-full bg-surface-container-lowest border border-outline-variant/15 rounded-lg p-5 font-mono text-sm text-on-surface-variant leading-loose resize-none outline-none focus:border-primary/40 transition-colors placeholder:text-on-surface-variant/30"
                />
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={saveNotes}
                    disabled={updateMutation.isPending}
                    className="flex items-center gap-1.5 text-primary text-sm font-semibold hover:underline disabled:opacity-50"
                  >
                    <span className="material-symbols-outlined text-lg">
                      {notesSaved ? 'check_circle' : 'save'}
                    </span>
                    {notesSaved ? 'Saved!' : 'Save Notes'}
                  </button>
                </div>
              </div>
            </section>
          </div>

          {/* Right column */}
          <div className="col-span-12 lg:col-span-4 space-y-8">
            {/* Status & Quick Actions */}
            <section className="bg-surface-container p-6 rounded-xl border border-primary/20 shadow-xl relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-2 opacity-10 group-hover:opacity-20 transition-opacity">
                <span className="material-symbols-outlined text-6xl">insights</span>
              </div>
              <h3 className="text-base font-bold text-primary mb-5 flex items-center gap-2">
                <span className="material-symbols-outlined">bolt</span>
                Application Status
              </h3>
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant mb-2">Current Stage</p>
                  <span className={`inline-flex px-3 py-1.5 text-sm font-bold rounded-lg ${STATUS_STYLE[job.status]}`}>
                    {STATUS_LABELS[job.status]}
                  </span>
                </div>
                <div className="pt-4 border-t border-outline-variant/15">
                  <p className="text-xs font-bold uppercase tracking-widest text-primary mb-3">Next Steps</p>
                  <ul className="space-y-3">
                    {job.status === 'APPLIED' && (
                      <>
                        <li className="flex items-center gap-3 text-sm">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                          Follow up if no response in 1 week
                        </li>
                        <li className="flex items-center gap-3 text-sm">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                          Research company culture & values
                        </li>
                      </>
                    )}
                    {job.status === 'HR' && (
                      <>
                        <li className="flex items-center gap-3 text-sm">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                          Prepare elevator pitch
                        </li>
                        <li className="flex items-center gap-3 text-sm">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                          Review salary expectations
                        </li>
                      </>
                    )}
                    {job.status === 'TECH' && (
                      <>
                        <li className="flex items-center gap-3 text-sm">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                          Practice system design questions
                        </li>
                        <li className="flex items-center gap-3 text-sm">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                          Review data structures & algorithms
                        </li>
                      </>
                    )}
                    {job.status === 'OFFER' && (
                      <>
                        <li className="flex items-center gap-3 text-sm">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                          Review offer details carefully
                        </li>
                        <li className="flex items-center gap-3 text-sm">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                          Negotiate salary & benefits
                        </li>
                      </>
                    )}
                    {job.status === 'REJECTED' && (
                      <li className="flex items-center gap-3 text-sm">
                        <span className="w-1.5 h-1.5 rounded-full bg-on-surface-variant shrink-0" />
                        Request feedback from recruiter
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </section>

            {/* Application Materials */}
            <section className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold tracking-tight">Application Documents</h3>
                  <p className="mt-1 text-xs text-on-surface-variant">
                    Store every file the company sent you without cluttering the page.
                  </p>
                </div>
                <button
                  onClick={() => setShowDocumentsModal(true)}
                  className="shrink-0 flex items-center gap-2 rounded-md border border-primary/20 bg-primary/10 px-4 py-2 text-sm font-semibold text-primary transition-all hover:bg-primary/20"
                >
                  <span className="material-symbols-outlined text-base">upload_file</span>
                  Upload
                </button>
              </div>

              <div className="overflow-hidden rounded-xl border border-outline-variant/10 bg-surface-container-low">
                {job.documents && job.documents.length > 0 ? (
                  <>
                    <div className="flex items-center justify-between border-b border-outline-variant/10 px-4 py-3 text-[11px] font-bold uppercase tracking-widest text-on-surface-variant">
                      <span>{job.documents.length} file{job.documents.length === 1 ? '' : 's'}</span>
                      <span className="text-on-surface-variant/60">Newest first</span>
                    </div>
                    <div className="max-h-[420px] overflow-y-auto divide-y divide-outline-variant/10">
                      {job.documents.map((document) => (
                        <ApplicationDocumentRow key={document.id} jobId={id!} document={document} />
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
                    <span className="material-symbols-outlined text-4xl text-on-surface-variant/40">folder_open</span>
                    <p className="text-sm text-on-surface-variant">No application documents uploaded yet</p>
                    <p className="text-xs text-on-surface-variant/60">
                      Upload PDFs, DOCX, briefs, take-homes or any file related to this process.
                    </p>
                  </div>
                )}
              </div>
            </section>

            {/* Process Signals / Timeline */}
            <section className="bg-surface-container-low rounded-xl p-6 border border-outline-variant/10 space-y-6">
              <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-on-surface-variant">Process Signals</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-on-surface-variant">Current Stage</span>
                  <span className="font-bold text-on-surface">{STATUS_LABELS[job.status]}</span>
                </div>
                <div className="space-y-3 pt-2">
                  <div className="relative pl-6 border-l border-outline-variant/30 space-y-4">
                    {/* Build a timeline from status progression */}
                    {(['APPLIED', 'HR', 'TECH', 'OFFER'] as JobStatus[])
                      .filter((s) => {
                        const order = ['APPLIED', 'HR', 'TECH', 'OFFER'];
                        return order.indexOf(s) <= order.indexOf(job.status === 'REJECTED' ? 'APPLIED' : job.status);
                      })
                      .reverse()
                      .map((s, i) => (
                        <div key={s} className="relative">
                          <div
                            className={`absolute -left-[27px] top-1.5 w-2 h-2 rounded-full border-4 border-background ${
                              i === 0 ? 'bg-primary ring-2 ring-primary/20' : 'bg-outline-variant'
                            }`}
                          />
                          <p className={`text-xs font-bold ${i === 0 ? 'text-on-surface' : 'text-on-surface-variant'}`}>
                            {STATUS_LABELS[s]}
                          </p>
                          {s === 'APPLIED' && (
                            <p className="text-[10px] text-on-surface-variant">{appliedDate}</p>
                          )}
                        </div>
                      ))}
                    {job.status === 'REJECTED' && (
                      <div className="relative">
                        <div className="absolute -left-[27px] top-1.5 w-2 h-2 rounded-full border-4 border-background bg-error ring-2 ring-error/20" />
                        <p className="text-xs font-bold text-error">Rejected</p>
                        <p className="text-[10px] text-on-surface-variant">{updatedDate}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>

      {showStatusModal && (
        <StatusModal
          current={job.status}
          onClose={() => setShowStatusModal(false)}
          onSave={(status) => updateMutation.mutate({ status })}
        />
      )}
      {showInterviewModal && (
        <AddInterviewModal
          onClose={() => {
            setShowInterviewModal(false);
            setUploadProgress(null);
            uploadInterviewMutation.reset();
          }}
          onCancelUpload={handleCancelUpload}
          onSubmit={(payload) => uploadInterviewMutation.mutate(payload)}
          isSubmitting={uploadInterviewMutation.isPending}
          uploadProgress={uploadProgress}
          error={
            uploadInterviewMutation.error instanceof DOMException &&
            uploadInterviewMutation.error.name === 'AbortError'
              ? null
              : (uploadInterviewMutation.error?.message ?? null)
          }
        />
      )}
      {showDocumentsModal && (
        <AddDocumentsModal
          onClose={() => setShowDocumentsModal(false)}
          onSubmit={(files) => uploadDocumentsMutation.mutate(files)}
          isSubmitting={uploadDocumentsMutation.isPending}
        />
      )}
    </div>
  );
}
